/**
 * Gift surface visibility SSOT — visible = entitled(block) && runtimeFlagEnabled.
 */
import type { CustomerFeatureFlags } from '../../config/customerFeatureFlags.js';
import type { CustomerEntitlementBlockKey } from '../entitlements/customerEntitlementGuards.js';

export interface GiftSurfaceVisibilityInput {
  readonly isBlockEntitled: (blockKey: CustomerEntitlementBlockKey) => boolean;
  readonly flags: CustomerFeatureFlags;
  readonly flagsReady: boolean;
  /** Platform chrome — entitlement query idle; gift flags alone gate surfaces (Wave C). */
  readonly platformChrome?: boolean;
}

export interface GiftSurfaceVisibility {
  readonly masterVisible: boolean;
  readonly donateVisible: boolean;
  readonly recurringVisible: boolean;
  readonly myDonationsVisible: boolean;
  readonly giftOverflowCount: number;
  readonly canShowGiftSurfaces: boolean;
  readonly flagsReady: boolean;
}

export function resolveGiftSurfaceVisibility(
  input: GiftSurfaceVisibilityInput,
): GiftSurfaceVisibility {
  const donationEntitled =
    input.platformChrome === true || input.isBlockEntitled('donation');
  const masterFlag = input.flags.customerGiftSurfacesMasterEnabled;
  const masterVisible = donationEntitled && masterFlag && input.flagsReady;

  const donateVisible =
    masterVisible && input.flags.customerGiftDonateMenuEnabled;
  const recurringVisible =
    masterVisible && input.flags.customerGiftRecurringDonationsEnabled;
  const myDonationsVisible =
    masterVisible && input.flags.customerGiftMyDonationsEnabled;

  const giftOverflowCount = [donateVisible, recurringVisible, myDonationsVisible].filter(
    Boolean,
  ).length;

  return {
    masterVisible,
    donateVisible,
    recurringVisible,
    myDonationsVisible,
    giftOverflowCount,
    canShowGiftSurfaces: masterVisible,
    flagsReady: input.flagsReady,
  };
}
